/**
 * RFPs in Flight widget — recently received RFPs from the sales inbox,
 * grouped by advertiser. Source: Firestore `processedRfps` collection.
 */

import React, { useMemo, useState } from 'react';
import { StatusPill } from '../ui';
import type { StatusVariant } from '../ui';
import { RfpInFlight } from '../../services/salesApi';
import WidgetHeader from './WidgetHeader';
import FilterChipGroup from './FilterChipGroup';

const statusVariant = (r: RfpInFlight): StatusVariant => {
  if (r.classification === 'RFP_UPDATE') return 'flagged';
  if (r.status === 'processing') return 'pending';
  if (r.daysSinceReceived <= 1) return 'live';
  if (r.daysSinceReceived <= 7) return 'approved';
  return 'draft';
};

interface Props {
  loading: boolean;
  error: string | null;
  rfps: RfpInFlight[];
}

const formatBudget = (rfp: RfpInFlight): string => {
  if (rfp.budget) return rfp.budget;
  if (rfp.budgetNumeric) {
    return `$${rfp.budgetNumeric.toLocaleString()}`;
  }
  return '—';
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface AdvertiserGroup {
  advertiser: string;
  rfps: RfpInFlight[];
  freshestDate: number;
  earliestDueDate: number | null;
  maxBudget: number | null;
}

type SortKey = 'latest' | 'due_soon' | 'budget';
type FilterKey = 'all' | 'new' | 'updates' | 'recent_7d';

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'latest',    label: 'Latest' },
  { value: 'due_soon',  label: 'Due soon' },
  { value: 'budget',    label: 'Budget' },
];

const FILTERS: { value: FilterKey; label: string }[] = [
  { value: 'all',        label: 'All' },
  { value: 'new',        label: 'New' },
  { value: 'updates',    label: 'Updates' },
  { value: 'recent_7d',  label: 'Last 7d' },
];

const RfpsInFlight: React.FC<Props> = ({ loading, error, rfps }) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filteredRfps = useMemo(() => {
    if (filter === 'all') return rfps;
    if (filter === 'updates') return rfps.filter((r) => r.classification === 'RFP_UPDATE');
    if (filter === 'new') return rfps.filter((r) => r.classification !== 'RFP_UPDATE');
    if (filter === 'recent_7d') return rfps.filter((r) => r.daysSinceReceived <= 7);
    return rfps;
  }, [rfps, filter]);

  const groups = useMemo<AdvertiserGroup[]>(() => {
    const byAdv = new Map<string, RfpInFlight[]>();
    filteredRfps.forEach((r) => {
      const key = r.advertiser || 'Unknown';
      const list = byAdv.get(key) || [];
      list.push(r);
      byAdv.set(key, list);
    });
    const built = Array.from(byAdv.entries()).map(([advertiser, list]) => {
      const sorted = list.sort((a, b) => b.emailDate.localeCompare(a.emailDate));
      const dueDates = list
        .map((r) => (r.dueDate ? new Date(r.dueDate).getTime() : NaN))
        .filter((n) => !Number.isNaN(n));
      const budgets = list.map((r) => r.budgetNumeric).filter((b): b is number => typeof b === 'number');
      return {
        advertiser,
        rfps: sorted,
        freshestDate: Math.max(...list.map((r) => new Date(r.emailDate).getTime())),
        earliestDueDate: dueDates.length > 0 ? Math.min(...dueDates) : null,
        maxBudget: budgets.length > 0 ? Math.max(...budgets) : null,
      };
    });

    built.sort((a, b) => {
      if (sortKey === 'due_soon') {
        // Groups with no due date sort last; soonest dates first.
        if (a.earliestDueDate === null && b.earliestDueDate === null) return b.freshestDate - a.freshestDate;
        if (a.earliestDueDate === null) return 1;
        if (b.earliestDueDate === null) return -1;
        return a.earliestDueDate - b.earliestDueDate;
      }
      if (sortKey === 'budget') {
        // Highest budget first; null budgets last.
        if (a.maxBudget === null && b.maxBudget === null) return b.freshestDate - a.freshestDate;
        if (a.maxBudget === null) return 1;
        if (b.maxBudget === null) return -1;
        return b.maxBudget - a.maxBudget;
      }
      return b.freshestDate - a.freshestDate;
    });
    return built;
  }, [filteredRfps, sortKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        g.advertiser.toLowerCase().includes(q) ||
        g.rfps.some(
          (r) =>
            r.agency?.toLowerCase().includes(q) ||
            r.emailFrom.toLowerCase().includes(q),
        ),
    );
  }, [groups, search]);

  const toggle = (advertiser: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(advertiser)) next.delete(advertiser);
      else next.add(advertiser);
      return next;
    });
  };

  return (
    <div style={styles.container}>
      <WidgetHeader
        title="RFPs in flight"
        subtitle={
          loading
            ? 'Loading…'
            : `${filteredRfps.length} of ${rfps.length} · grouped by advertiser`
        }
        right={
          <div style={styles.controls}>
            <input
              type="text"
              value={search}
              placeholder="Search advertiser or agency"
              onChange={(e) => setSearch(e.target.value)}
              style={styles.search}
            />
            <div style={styles.chipRow}>
              <FilterChipGroup options={FILTERS} value={filter} onChange={setFilter} size="sm" />
              <span style={styles.chipDivider} />
              <FilterChipGroup
                options={SORTS.map((s) => ({ value: s.value, label: <>↕ {s.label}</> }))}
                value={sortKey}
                onChange={setSortKey}
                size="sm"
              />
            </div>
          </div>
        }
      />

      {error && <div style={styles.error}>{error}</div>}

      {!loading && filtered.length === 0 && (
        <div style={styles.empty}>
          {rfps.length === 0
            ? 'No active RFPs in flight. Inbound RFPs are auto-detected from the sales inbox.'
            : 'No RFPs match the current search.'}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <ul style={styles.list}>
          {filtered.map((g) => {
            const isOpen = expanded.has(g.advertiser);
            const newest = g.rfps[0];
            return (
              <li key={g.advertiser} style={styles.row}>
                <button
                  type="button"
                  onClick={() => toggle(g.advertiser)}
                  style={styles.rowHeader}
                  aria-expanded={isOpen}
                >
                  <div style={styles.advertiserBlock}>
                    <span style={styles.advertiser}>{g.advertiser}</span>
                    <span style={styles.subtle}>
                      {g.rfps.length} RFP{g.rfps.length === 1 ? '' : 's'}
                      {newest.agency && ` · ${newest.agency}`}
                    </span>
                  </div>
                  <div style={styles.metaBlock}>
                    <span style={styles.budget}>{formatBudget(newest)}</span>
                    <span style={styles.subtle}>
                      Latest: {formatDate(newest.emailDate)} · {newest.daysSinceReceived}d ago
                    </span>
                  </div>
                  <StatusPill variant={statusVariant(newest)} size="sm">
                    {(newest.classification || newest.status).replace('_', ' ').toLowerCase()}
                  </StatusPill>
                  <span style={{ ...styles.chevron, transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                    ›
                  </span>
                </button>

                {isOpen && (
                  <div style={styles.detail}>
                    {g.rfps.map((r) => (
                      <div key={r.id} style={styles.rfpDetailRow}>
                        <div style={styles.rfpDetailMain}>
                          <div style={styles.subtle}>
                            From {r.emailFrom} on {formatDate(r.emailDate)}
                          </div>
                          <div style={styles.detailKvs}>
                            {r.budget && <span><strong>Budget:</strong> {r.budget}</span>}
                            {r.dueDate && <span><strong>Due:</strong> {r.dueDate}</span>}
                            {r.campaignStart && r.campaignEnd && (
                              <span>
                                <strong>Flight:</strong> {r.campaignStart} → {r.campaignEnd}
                              </span>
                            )}
                          </div>
                        </div>
                        {r.driveFolderUrl && (
                          <a
                            href={r.driveFolderUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.link}
                          >
                            Open folder ↗
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14 },
  controls: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' },
  search: {
    padding: '8px 12px',
    fontSize: 13,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    minWidth: 220,
    fontFamily: 'var(--font-sans)',
  },
  chipRow: { display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' },
  chipDivider: {
    width: 1,
    height: 14,
    background: 'var(--border-1)',
    margin: '0 4px',
  },
  error: {
    padding: '10px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
  },
  empty: {
    padding: '24px 12px',
    textAlign: 'center',
    color: 'var(--fg-4)',
    fontSize: 13,
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  row: {
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
  },
  rowHeader: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr auto auto auto',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'var(--font-sans)',
  },
  advertiserBlock: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  advertiser: { fontWeight: 600, color: 'var(--fg-1)', fontSize: 14 },
  subtle: { fontSize: 12, color: 'var(--fg-4)' },
  metaBlock: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 },
  budget: { fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--fg-2)', fontSize: 13 },
  chevron: {
    color: 'var(--fg-4)',
    fontSize: 18,
    transition: 'transform 160ms ease',
    width: 14,
    textAlign: 'center',
  },
  detail: {
    borderTop: '1px solid var(--border-1)',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    background: 'var(--bg-surface-2)',
  },
  rfpDetailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  rfpDetailMain: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  detailKvs: {
    display: 'flex',
    gap: 14,
    flexWrap: 'wrap',
    fontSize: 12.5,
    color: 'var(--fg-2)',
  },
  link: {
    color: 'var(--accent)',
    fontSize: 12.5,
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
};

export default RfpsInFlight;
